import { Settings, BarChart3, Target, Zap, LineChart, Shield, Layers, Code, Database, Activity, FileCode, Lock } from "lucide-react";
import SubServicePageLayout from "@/components/SubServicePageLayout";
import { MatrixCategory } from "@/components/CapabilityMatrix";

const services = [
  {
    name: "GA4 Strategy",
    icon: Target,
    components: ["Measurement planning", "KPI definition", "Data layer architecture"],
    outcome: "Analytics aligned with business objectives.",
  },
  {
    name: "Technical Implementation",
    icon: Settings,
    components: ["Property configuration", "Event tracking setup", "E-commerce tracking"],
    outcome: "Accurate data collection from day one.",
  },
  {
    name: "GTM Management",
    icon: Zap,
    components: ["Tag container setup", "Custom triggers and variables", "Server-side tagging"],
    outcome: "Flexible, maintainable tag infrastructure.",
  },
  {
    name: "Custom Reporting",
    icon: BarChart3,
    components: ["Exploration reports", "Looker Studio dashboards", "BigQuery exports"],
    outcome: "Insights accessible to all stakeholders.",
  },
  {
    name: "Migration Support",
    icon: LineChart,
    components: ["UA to GA4 migration", "Historical data preservation", "Team training"],
    outcome: "Smooth transition without data loss.",
  },
  {
    name: "Privacy Compliance",
    icon: Shield,
    components: ["Consent mode implementation", "Data retention policies", "PII protection"],
    outcome: "Analytics that respects user privacy.",
  },
];

const processSteps = [
  { number: "01", title: "Audit", description: "Review current tracking and identify gaps." },
  { number: "02", title: "Plan", description: "Define measurement strategy and data architecture." },
  { number: "03", title: "Implement", description: "Configure GA4, GTM, and all tracking." },
  { number: "04", title: "Validate", description: "Rigorous QA to ensure data accuracy." },
  { number: "05", title: "Enable", description: "Training and documentation for your team." },
];

const technologies = [
  { category: "Analytics", items: ["GA4", "GTM", "BigQuery", "Looker Studio"] },
  { category: "Testing", items: ["GA Debugger", "Tag Assistant", "DebugView"] },
];

const faqs = [
  { question: "How long does GA4 implementation take?", answer: "Typically 4-8 weeks depending on complexity." },
  { question: "Do you provide ongoing support?", answer: "Yes, we offer maintenance and optimization packages." },
  { question: "Can you migrate our historical UA data?", answer: "We can export and preserve historical data, though GA4 doesn't import UA data directly." },
];

const qualification = {
  forYou: ["You need accurate, actionable analytics", "You're migrating from UA to GA4", "You want proper tracking from the start"],
  notForYou: ["You're happy with default GA4 setup", "You don't have resources to act on data"],
};

// Capability Matrix for tracking capabilities and data layer depth
const capabilityCategories: MatrixCategory[] = [
  {
    id: "tracking",
    name: "Event Tracking",
    capabilities: [
      {
        id: "enhanced-ecommerce",
        name: "Enhanced E-commerce",
        icon: Layers,
        description: "Complete purchase funnel tracking from product views to transactions.",
        outcomes: [
          "Full visibility into shopping behavior",
          "Cart abandonment insights",
          "Revenue attribution by source",
        ],
        decisions: [
          "Optimize checkout flow based on drop-off data",
          "Personalize product recommendations",
          "Adjust pricing strategy by segment",
        ],
        failuresPrevented: [
          "Missing revenue data in reports",
          "Incorrect transaction attribution",
          "Blind spots in conversion funnel",
        ],
      },
      {
        id: "custom-events",
        name: "Custom Event Architecture",
        icon: Code,
        description: "Tailored event tracking aligned with your specific business actions.",
        outcomes: [
          "Track unique user interactions",
          "Measure micro-conversions",
          "Build custom audiences",
        ],
        decisions: [
          "Identify high-value user behaviors",
          "Optimize feature engagement",
          "Segment users by action patterns",
        ],
        failuresPrevented: [
          "Generic tracking that misses business context",
          "Inability to measure what matters",
          "Lost insights on custom features",
        ],
      },
      {
        id: "user-properties",
        name: "User Properties & Dimensions",
        icon: Activity,
        description: "Enrich user data with custom attributes for deeper segmentation.",
        outcomes: [
          "User-level insights beyond sessions",
          "Lifetime value tracking",
          "Cohort analysis capabilities",
        ],
        decisions: [
          "Personalize based on user attributes",
          "Target high-LTV segments",
          "Adjust acquisition by user quality",
        ],
        failuresPrevented: [
          "Treating all users the same",
          "Missing LTV context in reports",
          "Inability to segment by business logic",
        ],
      },
    ],
  },
  {
    id: "data-layer",
    name: "Data Layer",
    capabilities: [
      {
        id: "data-layer-architecture",
        name: "Data Layer Architecture",
        icon: Database,
        description: "Structured data layer design for reliable, scalable tracking.",
        outcomes: [
          "Consistent data across all pages",
          "Future-proof tracking foundation",
          "Clean separation of data and tags",
        ],
        decisions: [
          "Scale tracking without code changes",
          "Add new tags with confidence",
          "Debug issues systematically",
        ],
        failuresPrevented: [
          "Fragile tracking that breaks with site updates",
          "Inconsistent data between pages",
          "Tag conflicts and data loss",
        ],
      },
      {
        id: "gtm-container",
        name: "GTM Container Strategy",
        icon: FileCode,
        description: "Organized tag management with versioning and governance.",
        outcomes: [
          "Maintainable tag infrastructure",
          "Clear ownership and documentation",
          "Safe deployment with previews",
        ],
        decisions: [
          "Onboard new tracking requests efficiently",
          "Audit and optimize tag performance",
          "Rollback changes when needed",
        ],
        failuresPrevented: [
          "Chaotic container with orphan tags",
          "Untested changes breaking tracking",
          "No visibility into who changed what",
        ],
      },
      {
        id: "server-side",
        name: "Server-Side Tagging",
        icon: Lock,
        description: "First-party data collection for privacy compliance and accuracy.",
        outcomes: [
          "Bypass ad blockers for complete data",
          "Improved data accuracy",
          "Enhanced privacy control",
        ],
        decisions: [
          "Control exactly what data leaves your domain",
          "Enrich data before sending to vendors",
          "Extend cookie lifetimes compliantly",
        ],
        failuresPrevented: [
          "Data loss from browser restrictions",
          "Third-party cookie deprecation impact",
          "Privacy regulation violations",
        ],
      },
    ],
  },
];

const GA4Implementation = () => (
  <SubServicePageLayout
    seoTitle="GA4 Implementation Services"
    seoDescription="Expert Google Analytics 4 implementation. From measurement planning to custom reporting. Accurate data, actionable insights."
    canonicalUrl="https://xperfomax.com/analytics/ga4-implementation"
    category="analytics"
    categoryLabel="Analytics / GA4 Implementation"
    headline="GA4 implementation done right."
    subheadline="We implement Google Analytics 4 to capture the data that matters. Accurately, reliably, and in compliance with privacy regulations."
    problemTitle="Why most GA4 implementations fail."
    problems={["Default setup misses critical events.", "Poor data layer causes inaccurate tracking.", "No connection between analytics and business questions."]}
    systemTitle="Complete GA4 implementation."
    services={services}
    processTitle="From audit to enablement."
    processSteps={processSteps}
    technologies={technologies}
    capabilityMatrix={{
      title: "Tracking capabilities & data layer depth.",
      label: "Capabilities",
      description: "Explore our GA4 implementation capabilities and understand the depth of tracking we deliver.",
      categories: capabilityCategories,
    }}
    qualification={qualification}
    faqs={faqs}
    finalCtaTitle="Ready for analytics that work?"
    finalCtaDescription="Let's implement GA4 properly from the start."
  />
);

export default GA4Implementation;
